import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useCostingCalculationActions } from 'pricing/hooks';
import { IPricingProfileCostingCalculation } from 'pricing/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  pricingProfileCostingCalculations: IPricingProfileCostingCalculation[];
  buttonElement?: ReactElement;
  buttonLabel?: string;
  onRemoveSuccess?: () => void;
  disabled?: boolean;
  notificationPosition?: NotificationPosition;
}

export const RemovePricingProfileCostingCalculationsButton = ({
  pricingProfileCostingCalculations,
  buttonElement,
  buttonLabel,
  onRemoveSuccess,
  disabled = false,
  notificationPosition,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { removeCostingCalculations } = useCostingCalculationActions({
    selectionCount: pricingProfileCostingCalculations.length,
    position: notificationPosition,
  });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const removedCostingCalculationIds = pricingProfileCostingCalculations.map(({ id }) => id);

    const { success } = await removeCostingCalculations({ ids: removedCostingCalculationIds });

    if (!success) {
      return;
    }

    onRemoveSuccess?.();
  }, [pricingProfileCostingCalculations, removeCostingCalculations, onRemoveSuccess]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: disabled ? undefined : () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={disabled}
      />
    );
  }, [buttonElement, buttonLabel, disabled, t]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemoveCostingCalculations', {
          count: pricingProfileCostingCalculations.length,
        })}
        message={t('messageConfirmRemoveCostingCalculations', {
          count: pricingProfileCostingCalculations.length,
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
